import React, { useState, useCallback, useMemo } from "react"
import { clsx } from "clsx"
import {
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline"
import { Button } from "../../../components/Primitives/Button"
import { Text } from "../../../components/Primitives/Text"
import { Icon } from "../../../components/Primitives/Icon"
import { MarkdownRenderer } from "../../../components/Primitives/MarkdownRenderer"
import { Spinner } from "../../../components/Primitives/Spinner"
import { useConfig } from "../../../hooks/useConfig"
import {
  QuizQuestionChoice,
  QuizResultLink,
  QuizResult,
} from "../../../types/quiz"
import QUIZ, { getInitialState } from "../../../mocks/quiz"

// Import QuizState type from mocks
import type { QuizState } from "../../../mocks/quiz"

// ========================================================================================
// Constants & Configuration
// ========================================================================================

const STYLES = {
  // Main layout
  quiz: "min-h-screen w-full bg-white relative overflow-hidden border border-border/20 shadow-2xl",
  main: "w-full h-full flex flex-col relative pt-14 px-4 xs:pt-18",

  // Header styles
  header:
    "absolute top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-30 flex items-center justify-between h-11 px-3 3xs:h-12 3xs:px-4 2xs:h-13 2xs:px-5 xs:h-14 xs:px-6",
  backButton:
    "absolute left-2 flex items-center text-gray-700 px-2 py-1 h-7 3xs:px-3 3xs:py-2 3xs:h-8 2xs:px-4 2xs:py-2 2xs:h-9 xs:gap-1 xs:px-5 xs:py-2 xs:h-10",
  progressContainer: "flex-1 mx-8 3xs:mx-10 2xs:mx-12 xs:mx-16",
  progressBar:
    "w-full bg-gray-200 rounded-full overflow-hidden h-1 3xs:h-1.5 2xs:h-2 xs:h-2.5",
  progressFill:
    "h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all duration-700 ease-out rounded-full",

  // Content styles
  questionSection:
    "flex-1 flex flex-col items-center justify-start transition-all duration-300 ease-out space-y-6",
  questionContent: "w-full mx-auto text-center max-w-2xl space-y-6",
  questionHeader: "space-y-4 2xs:space-y-5 xs:space-y-6",
  questionTitle: "space-y-2 2xs:space-y-4 xs:space-y-3",
  questionTitleText: "text-foreground leading-tight max-w-2xl mx-auto",
  choicesContainer: "flex flex-wrap justify-center gap-3 max-w-2xl mx-auto",
  choiceButton:
    "flex-1 min-w-[240px] max-w-[400px] h-auto text-left bg-purple-50 hover:bg-purple-100 transition-all duration-300 ease-out hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] min-h-[64px] rounded-xl p-4",
  choiceText: "leading-relaxed text-foreground text-center",

  // Interstitial styles
  interstitialSection:
    "min-h-[calc(100vh-88px)] w-full flex flex-col items-center justify-center text-center transition-all duration-300 ease-out space-y-6",
  interstitialContent:
    "w-full max-w-3xl mx-auto space-y-6 3xs:space-y-7 2xs:space-y-3",
  imageContainer: "flex justify-center",
  image:
    "rounded-xl shadow-lg aspect-square overflow-hidden bg-gradient-to-br from-purple-100 to-blue-100 w-45 h-45 3xs:w-50 3xs:h-50 2xs:w-48 2xs:h-48",
  interstitialText: "text-center space-y-3 2xs:space-y-4 xs:space-y-1 xs:mb-5",
  interstitialMessage:
    "text-base 3xs:text-lg 2xs:text-xl xs:text-2xl text-foreground leading-relaxed max-w-2xl mx-auto",
  buttonContainer: "flex justify-center",

  // Result styles
  resultSection:
    "flex-1 flex flex-col items-center justify-start text-center transition-all duration-300 ease-out space-y-4",
  resultContent:
    "w-full max-w-4xl mx-auto space-y-4 3xs:space-y-5 2xs:space-y-6 xs:space-y-4",
  resultHeader: "space-y-2 2xs:space-y-3 xs:space-y-4",
  resultCard:
    "text-sm px-3 py-4 rounded-lg 3xs:text-base 3xs:px-4 3xs:py-5 3xs:rounded-xl 2xs:text-lg 2xs:px-5 2xs:py-6 2xs:rounded-xl xs:text-lg xs:px-6 xs:py-8 xs:rounded-2xl text-gray-600 leading-relaxed max-w-2xl mx-auto text-center border border-purple-100 bg-purple-50",
  resultActions: "space-y-2",
  loaderContainer: "flex flex-col justify-center items-center py-8 gap-4",

  // Shared styles
  divider:
    "bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg mx-auto shadow-sm",
  responsiveText: {
    backLabel: "hidden xs:text-sm xs:block",
    title: "text-2xl 3xs:text-3xl text-gray-900 leading-tight pt-2",
    resultType:
      "text-2xl 3xs:text-2xl 2xs:text-3xl xs:text-3xl text-gray-800 leading-tight text-purple-700",
  },
  responsiveButton: {
    primary:
      "w-full px-6 py-2 text-sm 3xs:px-8 3xs:py-2 3xs:text-base 2xs:w-fit 2xs:px-10 2xs:py-3 2xs:text-base xs:px-12 xs:py-2 xs:text-lg",
    secondary:
      "w-full px-4 py-2 text-sm 3xs:w-fit 3xs:px-6 3xs:py-2 3xs:text-base 2xs:px-8 2xs:py-2 2xs:text-base xs:px-10 xs:py-2 xs:text-base",
  },
  responsiveIcon: {
    arrow:
      "text-purple-700 stroke-[2.5px] w-4 h-4 3xs:w-4 3xs:h-4 2xs:w-5 2xs:h-5 xs:w-5 xs:h-5",
    external: "text-white w-4 h-4 2xs:w-5 2xs:h-5 xs:w-5 xs:h-5",
  },
  responsiveDivider: {
    question: "w-20 h-1 3xs:w-24 3xs:h-1 2xs:w-28 2xs:h-1.5 xs:w-32 xs:h-2",
    result: "w-16 h-1 3xs:w-20 3xs:h-1 2xs:w-24 2xs:h-1.5 xs:w-32 xs:h-2",
  },
} as const

const MARKDOWN_CLASSES = {
  question:
    "mt-0 [&>*]:mt-0 [&>*:first-child]:mt-0 [&>*]:leading-tight [&>*]:font-semibold text-lg 3xs:text-xl 2xs:text-2xl xs:text-3xl",
  choice:
    "mt-0 [&>*]:mt-0 [&>*:first-child]:mt-0 [&>*]:leading-tight text-sm 3xs:text-sm 2xs:text-base xs:text-base",
  interstitial:
    "mt-0 [&>*]:mt-0 [&>*:first-child]:mt-0 [&>*]:leading-relaxed text-base 3xs:text-lg 2xs:text-xl",
  result:
    "mt-0 [&>*]:mt-0 [&>*:first-child]:mt-0 text-base 3xs:text-lg 2xs:text-xl xs:text-xl",
} as const

// ========================================================================================
// Type Definitions
// ========================================================================================

type StepType = "question" | "interstitial" | "result"

interface CurrentData {
  type: StepType
  data: any
}

interface QuestionViewProps {
  question: any
  transitionClass: string
  onChoiceSelect: (choice: QuizQuestionChoice) => void
}

interface InterstitialViewProps {
  interstitial: any
  transitionClass: string
  onContinue: () => void
  t: (key: string) => string
}

interface ResultViewProps {
  result: any
  transitionClass: string
  onResultLink: (link: QuizResultLink) => void
  onRestart: () => void
  t: (key: string) => string
  isLoading: boolean
}

interface HeaderProps {
  canGoBack: boolean
  onBack: () => void
  progress: number
  t: (key: string) => string
}

// ========================================================================================
// UI Components
// ========================================================================================

/**
 * Question view component with choices
 */
const QuestionView = React.memo<QuestionViewProps>(
  ({ question, transitionClass, onChoiceSelect }) => (
    <section className={clsx(STYLES.questionSection, transitionClass)}>
      <article className={STYLES.questionContent}>
        <header className={STYLES.questionHeader}>
          <hgroup className={STYLES.questionTitle}>
            <div className={STYLES.questionTitleText}>
              <MarkdownRenderer
                variant="paragraph"
                content={question.value}
                className={MARKDOWN_CLASSES.question}
              />
            </div>
            <hr
              className={clsx(
                STYLES.divider,
                STYLES.responsiveDivider.question,
              )}
            />
          </hgroup>
        </header>

        <nav
          className={STYLES.choicesContainer}
          role="group"
          aria-label="Quiz choices"
        >
          {question.choices.map((choice: QuizQuestionChoice) => (
            <button
              key={choice.code}
              onClick={() => onChoiceSelect(choice)}
              className={STYLES.choiceButton}
              type="button"
            >
              <span className={STYLES.choiceText}>
                <MarkdownRenderer
                  variant="paragraph"
                  content={choice.value}
                  className={MARKDOWN_CLASSES.choice}
                />
              </span>
            </button>
          ))}
        </nav>
      </article>
    </section>
  ),
)

/**
 * Interstitial view component with image and continue button
 */
const InterstitialView = React.memo<InterstitialViewProps>(
  ({ interstitial, transitionClass, onContinue, t }) => (
    <section className={clsx(STYLES.interstitialSection, transitionClass)}>
      <article className={STYLES.interstitialContent}>
        <figure className={STYLES.imageContainer}>
          <div className={STYLES.image}>
            <img
              src={interstitial.image}
              alt="Feedback illustration"
              className="w-full h-full object-cover"
            />
          </div>
        </figure>

        <div className={STYLES.interstitialText}>
          <div className={STYLES.interstitialMessage}>
            <MarkdownRenderer
              variant="paragraph"
              content={interstitial.value}
              className={MARKDOWN_CLASSES.interstitial}
            />
          </div>
          <hr
            className={clsx(STYLES.divider, STYLES.responsiveDivider.question)}
          />
        </div>

        <footer className={STYLES.buttonContainer}>
          <Button
            variant="default"
            onClick={onContinue}
            className={STYLES.responsiveButton.primary}
          >
            {t("config_quiz_next_button")}
          </Button>
        </footer>
      </article>
    </section>
  ),
)

/**
 * Result view component with result display and action buttons
 */
const ResultView = React.memo<ResultViewProps>(
  ({ result, transitionClass, onResultLink, onRestart, t, isLoading }) => (
    <section className={clsx(STYLES.resultSection, transitionClass)}>
      <article className={STYLES.resultContent}>
        <header className={STYLES.resultHeader}>
          <Text as="h1" className={STYLES.responsiveText.title}>
            {result.header}
          </Text>
          <hr
            className={clsx(STYLES.divider, STYLES.responsiveDivider.result)}
          />
        </header>

        <main className={STYLES.resultCard}>
          {isLoading ? (
            <div className={STYLES.loaderContainer}>
              <Spinner size="lg" />
              <Text className={STYLES.responsiveText.resultType}>
                {t("config_quiz_loading_result")}
              </Text>
            </div>
          ) : (
            <MarkdownRenderer
              variant="paragraph"
              content={result.description}
              className={MARKDOWN_CLASSES.result}
            />
          )}
        </main>

        <footer className={STYLES.resultActions}>
          <div className={STYLES.buttonContainer}>
            <Button
              disabled={isLoading}
              variant="default"
              onClick={() => onResultLink(result.link)}
              className={clsx(
                STYLES.responsiveButton.primary,
                "flex items-center gap-2",
              )}
            >
              {result.link?.label}
              {result.link?.type === "link" && (
                <Icon
                  icon={ArrowTopRightOnSquareIcon}
                  className={STYLES.responsiveIcon.external}
                />
              )}
            </Button>
          </div>
          <div className="text-center">
            <Button
              variant="ghost"
              onClick={onRestart}
              className={clsx(
                STYLES.responsiveButton.secondary,
                "text-gray-600 hover:text-gray-900 hover:bg-gray-100",
              )}
            >
              {t("config_quiz_restart_button")}
            </Button>
          </div>
        </footer>
      </article>
    </section>
  ),
)

/**
 * Header component with back button and progress bar
 */
const Header = React.memo<HeaderProps>(({ canGoBack, onBack, progress, t }) => (
  <header className={STYLES.header}>
    <Button
      variant="ghost"
      size="sm"
      onClick={onBack}
      disabled={!canGoBack}
      className={clsx(
        STYLES.backButton,
        "hover:bg-transparent focus:bg-transparent",
      )}
      aria-label="Go back to previous step"
    >
      <Icon icon={ArrowLeftIcon} className={STYLES.responsiveIcon.arrow} />
      <span className={STYLES.responsiveText.backLabel}>
        {t("config_quiz_back_button")}
      </span>
    </Button>

    <div
      className={STYLES.progressContainer}
      role="progressbar"
      aria-label="Quiz progress"
    >
      <div className={STYLES.progressBar}>
        <div
          className={STYLES.progressFill}
          style={{ width: `${progress}%` }}
          aria-label={`Progress: ${progress}%`}
        />
      </div>
    </div>
  </header>
))

// Add display names for better debugging
QuestionView.displayName = "QuestionView"
InterstitialView.displayName = "InterstitialView"
ResultView.displayName = "ResultView"
Header.displayName = "Header"

export function QuizPage() {
  const { t } = useConfig()
  const [state, setState] = useState<QuizState>(getInitialState())
  const [isResultLoading, setIsResultLoading] = useState(false)

  const getNextStep = (choiceCode: string) => QUIZ.tree[choiceCode]

  /**
   * Find quiz result by code, fallback to first result
   */
  const findResult = (resultCode: string): QuizResult =>
    QUIZ.results.find((r) => r.code === resultCode) || QUIZ.results[0]

  /**
   * Get current data based on step type
   */
  const getCurrentData = (currentStep: string): CurrentData => {
    const stepTypeMap = {
      question: () => ({
        type: "question" as const,
        data: QUIZ.questions.find((q) => q.code === currentStep),
      }),
      interstitial: () => ({
        type: "interstitial" as const,
        data: QUIZ.interstitials.find((i) => i.code === currentStep),
      }),
      result: () => ({
        type: "result" as const,
        data: findResult(currentStep),
      }),
    }

    if (currentStep.startsWith("question_")) return stepTypeMap.question()
    if (currentStep.startsWith("interstitial_"))
      return stepTypeMap.interstitial()
    if (currentStep.startsWith("result_")) return stepTypeMap.result()

    return { type: "result", data: QUIZ.results[0] }
  }

  // Activar loader automáticamente cuando se llega al resultado
  React.useEffect(() => {
    if (state.currentStep.startsWith("result_")) {
      setIsResultLoading(true)
      const timer = setTimeout(() => {
        setIsResultLoading(false)
      }, 400)
      return () => clearTimeout(timer)
    }
  }, [state.currentStep])

  // ========================================================================================
  // Computed Values
  // ========================================================================================

  const currentData = useMemo(
    () => getCurrentData(state.currentStep),
    [state.currentStep],
  )

  const progress = useMemo(() => {
    return state.progressHistory[state.progressHistory.length - 1]
  }, [state.progressHistory])

  const canGoBack = state.stepHistory.length > 1

  const transitionClass = useMemo(() => {
    if (state.isTransitioning) {
      return state.transitionDirection === "up"
        ? "opacity-0 transform translate-y-[-100px] scale-95 transition-all duration-300 ease-out"
        : "opacity-0 transform translate-y-[100px] scale-95 transition-all duration-300 ease-out"
    }

    return state.transitionDirection === "down"
      ? "opacity-100 transform translate-y-0 scale-100 transition-all duration-300 ease-out"
      : "opacity-100 transform translate-y-0 scale-100 transition-all duration-300 ease-out"
  }, [state.isTransitioning, state.transitionDirection])

  // ========================================================================================
  // Event Handlers
  // ========================================================================================

  /**
   * Navigate to next step with optional choice tracking
   */
  const navigateToStep = useCallback(
    (nextStep: string, choiceCode?: string, choiceProgress?: number) => {
      // Step 1: Animate current content out (slide up)
      setState((prev) => ({
        ...prev,
        isTransitioning: true,
        transitionDirection: "up",
      }))

      // Step 2: After slide up completes, change content and slide down
      setTimeout(() => {
        setState((prev) => {
          const currentProgress =
            prev.progressHistory[prev.progressHistory.length - 1] || 0
          const newProgress =
            choiceProgress !== undefined ? choiceProgress : currentProgress

          return {
            currentStep: nextStep,
            stepHistory: [...prev.stepHistory, nextStep],
            answers: choiceCode
              ? { ...prev.answers, [prev.currentStep]: choiceCode }
              : prev.answers,
            progressHistory: [...prev.progressHistory, newProgress],
            isTransitioning: false,
            transitionDirection: "down",
          }
        })
      }, 300) // Match the CSS transition duration
    },
    [],
  )

  /**
   * Handle quiz choice selection and navigation
   */
  const handleChoiceSelect = useCallback(
    (choice: QuizQuestionChoice) => {
      const nextStep = getNextStep(choice.code)

      if (!nextStep) {
        navigateToStep("result_1", choice.code, choice.progress)
        return
      }

      if (Array.isArray(nextStep)) {
        navigateToStep(nextStep[0], choice.code, choice.progress)
      } else {
        navigateToStep(nextStep, choice.code, choice.progress)
      }
    },
    [navigateToStep],
  )

  /**
   * Handle interstitial continue button
   */
  const handleInterstitialContinue = useCallback(() => {
    const answerThatLedHere = Object.entries(QUIZ.tree).find(
      ([, step]) => Array.isArray(step) && step[0] === state.currentStep,
    )?.[0]

    if (answerThatLedHere) {
      const nextStep = QUIZ.tree[answerThatLedHere]
      if (Array.isArray(nextStep) && nextStep[1]) {
        navigateToStep(nextStep[1])
      }
    }
  }, [state.currentStep, navigateToStep])

  /**
   * Handle back navigation WITHOUT answer cleanup
   */
  const handleBack = useCallback(() => {
    if (!canGoBack) return

    setState((prev) => {
      const newHistory = prev.stepHistory.slice(0, -1)
      const previousStep = newHistory[newHistory.length - 1]
      const newProgressHistory = prev.progressHistory.slice(0, -1)

      return {
        currentStep: previousStep,
        stepHistory: newHistory,
        answers: prev.answers, // Keep all answers - no cleanup
        progressHistory: newProgressHistory,
        isTransitioning: false,
        transitionDirection: "none",
      }
    })
  }, [canGoBack])

  /**
   * Reset quiz to initial state
   */
  const handleRestart = useCallback(() => setState(getInitialState()), [])

  const handleResultLink = useCallback((link: QuizResultLink) => {
    if (link.type === "link") {
      window.open(link.uri, "_blank")
    } else if (link.type === "reference") {
      const message = {
        action: "scrollTo",
        targetId: link.uri,
      }

      // Send the message to the parent window
      // Use '*' for the targetOrigin for simplicity during development,
      // but for production, you should specify the parent window's origin for security.
      window.parent.postMessage(message, "*")
    }
  }, [])

  // ========================================================================================
  // Render Logic
  // ========================================================================================

  /**
   * Render appropriate content based on current step type
   */
  const renderContent = () => {
    if (!currentData.data) return null

    switch (currentData.type) {
      case "question":
        return (
          <QuestionView
            question={currentData.data}
            transitionClass={transitionClass}
            onChoiceSelect={handleChoiceSelect}
          />
        )
      case "interstitial":
        return (
          <InterstitialView
            interstitial={currentData.data}
            transitionClass={transitionClass}
            onContinue={handleInterstitialContinue}
            t={t}
          />
        )
      case "result":
        return (
          <ResultView
            result={currentData.data}
            transitionClass={transitionClass}
            onResultLink={handleResultLink}
            onRestart={handleRestart}
            t={t}
            isLoading={isResultLoading}
          />
        )
      default:
        console.error(`Unknown step type: ${currentData.type}`)
        return null
    }
  }

  // ========================================================================================
  // Component Render
  // ========================================================================================

  return (
    <div
      className={STYLES.quiz}
      role="application"
      aria-label="Interactive quiz"
    >
      <main className={STYLES.main}>
        {renderContent()}
        <Header
          canGoBack={canGoBack}
          onBack={handleBack}
          progress={progress}
          t={t}
        />
      </main>
    </div>
  )
}
